import { inject, Injectable, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { jwtDecode } from "jwt-decode";
import { MONTHS } from '../constants/date.constants';
import { HttpService } from './http.service';
import { LOGIN, SERVER_URL } from '../constants/apis-list.constant';
import { SharedDataService } from './shared-data.service';
import { Router } from '@angular/router';
import { LocalStorageService } from './local-storage.service';
import { ROUTES } from '../constants/routes.constant';
import { PrivacyPolicyModalComponent } from '../shared/components/privacy-policy-modal/privacy-policy-modal.component';
import { TermsOfUseModalComponent } from '../shared/components/terms-of-use-modal/terms-of-use-modal.component';

declare const Buffer: any;

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  constructor() { }
  httpService = inject(HttpService);
  sharedService = inject(SharedDataService);
  router = inject(Router);
  localStorageService = inject(LocalStorageService);

  @ViewChild(TermsOfUseModalComponent) termUseModalComp!: TermsOfUseModalComponent;
  @ViewChild(PrivacyPolicyModalComponent) privacyPolicyModalComp!: PrivacyPolicyModalComponent;
  

  public uploadMedia(event: any): any {
    return new Promise((resolve, reject) => {
      if (event.target.files && event.target.files.length) {
        const media = event.target.files[0];
        const data: any = {
          src: '',
          formData: media,
          type: media.type,
          name: media.name,
        };
        if (media.type.indexOf('image') === -1) {
          data.src = data.formData;
          resolve(data);
        } else {
          const reader = new FileReader();
          const [file] = event.target.files;
          reader.readAsDataURL(file);
          reader.onload = () => {
            data.src = reader.result as string;
            resolve(data);
          };
        }
      } else {
        reject();
      }
    });
  }

  /**
   * The following method is used to convert the respective base64encoded data into a blob
   * @param dataURI
   * @returns {Blob}
   */
  public dataURItoBlob(dataURI: any): Blob {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = this.getMimetypeFromDataUri(dataURI);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const dataView = new DataView(ab);
    return new Blob([dataView], { type: mimeString });
  }

  /**
   * The following method is used to convert the respective base64encoded data into a blob
   * @param unit8Array
   * @param type
   * @returns {Blob}
   */
  public Unit8ArrayToFile(unit8Array: any, type: string = ''): Blob {
    const byteString = atob(unit8Array.toString('base64'));
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    return new Blob([int8Array], { type });
  }

  /**
   * the following makes sure the given string is not null or empty
   * @param {string} value
   * @returns {boolean}
   */
  public isNotEmptyOrUndefined(value: any) {
    return value !== undefined || value !== '' || value.length > 0;
  }

  /**
   * The following method is used to convert the image file into Data URI
   * @param imageFile
   * @param callback
   */
  public imageFileToDataURI(imageFile: any, callback: any) {
    const file: File = imageFile,
      myReader: any = new FileReader();

    myReader.onloadend = (e: any) => {
      /*const trimIndex = myReader?.result.toString().indexOf('base64,');
      callback(myReader?.result.toString().substr(trimIndex + 7));*/
      callback(myReader?.result.toString());
    };

    myReader.readAsDataURL(file);
  }

  /**
   * The following method is used to convert the image file into Data URI
   * @param base64Img
   * @param callback
   */
  public formatBase64Image(base64Img: string, callback: any) {
    const img = base64Img;
    const trimIndex = img.indexOf('base64,');
    callback(img.substr(trimIndex + 7));
  }

  /**
   * The following method is used to check the dimensions of image
   * @param imageFile
   * @param callback
   */
  public imageFileDimensions(imageFile: any, callback: any) {
    const fReader: any = new FileReader();
    fReader.onload = function () {
      const img = new Image();
      img.onload = function () {
        callback(img.width, img.height);
      };

      img.src = fReader.result.toString();
    };

    fReader.readAsDataURL(imageFile);
  }

  /**
   * The following checks if the given is a valid base64 string or not
   * @param {string} base64Img
   * @returns {boolean}
   */
  public isValidBase64(base64Img: string) {
    try {
      atob(base64Img);
      return true;
    } catch (e) {
      return false;
    }
  }

  public generateSeoFriendlyUrl(link: string, name: any, id: number | string): string {
    const slugName = name ? this.formatString(name).toLowerCase() : null;
    if (id) {
      return `${link}/${slugName ? slugName + '-' + id : id}`;
    }
    return `${link}/${slugName ? slugName : ''}`;
  }

  public getIdFromSeoFriendlyUrl(link: string | null): any {
    if (!link) {
      return null;
    }
    const numberRegex = new RegExp(/^\d+$/);
    // Return if link is already an id
    if (numberRegex.test(link)) {
      return Number(link);
    }
    const id = link.split(/[-]+/).pop();
    return id && numberRegex.test(id) ? Number(id) : null;
  }

  public getMimetypeFromDataUri(dataURI: string): string {
    return dataURI.split(',')[0].split(':')[1].split(';')[0];
  }

  public formatString(str: string = ''): string {
    return `${str}`.replace(/[^a-z0-9_]+/gi, '-').replace(/^-|-$/g, '');
  }

  getNestedPropertyValue(theObject: any, path: string, separator = '.'): string {
    try {
      separator = separator || '.';
      const pathsList = path.replace('[', separator).replace(']', '').split(separator);
      return pathsList.reduce((obj, property, index) => {
        // Checking for array
        if (property.indexOf('*') > -1) {
          if (Array.isArray(obj)) {
            return obj || [];
          }
          return (obj[pathsList[index - 1]] || []).map((i: any) => {
            return i[pathsList[index + 1]];
          });
        }
        if (Array.isArray(obj)) {
          return obj.map((i: any) => {
            return i[property];
          });
        }
        return obj[property];
      }, theObject);
    } catch (err) {
      return '';
    }
  }

  public getImageDimensions(image: File, cb: Function) {
    const reader = new FileReader();
    reader.readAsDataURL(image);
    reader.onload = () => {
      const img = new Image();
      img.onload = function () {
        // image is loaded; sizes are available
        cb({
          width: img.width,
          height: img.height,
        });
      };
      img.src = reader.result as string; // is the data URL because called with readAsDataURL
    };
  }

  /**
   * Following method will convert base64 to File
   * @param dataurl
   * @param filename
   * @returns
   */
  public dataURLtoFile(dataurl: any, filename: any) {
    var arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[arr.length - 1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Following method will remove requested props and return a new object
   * @param obj
   * @param keys
   * @returns
   */
  public objectWithoutProperties(obj: any, keys: string[]) {
    const target: any = {};
    for (var i in obj) {
      if (keys.indexOf(i) >= 0) continue;
      if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
      target[i] = obj[i];
    }
    return target;
  }
  dateFormatChanger = (date: string) => { //toIsoFormat
    const dateArray = date.split("-")
    let newDate = dateArray[2] + "-" + dateArray[0] + "-" + dateArray[1]
    return newDate
  }
  dateFormatReverter = (date: string) => { //FromIso
    const dateArray = date.split("-")
    let newDate = dateArray[1] + "-" + dateArray[2] + "-" + dateArray[0]
    return newDate
  }
  calculateAge(birthday: string) { // birthday is a date
    let bDay = new Date(birthday)
    let ageDifMs = Date.now() - bDay.getTime();
    let ageDate = new Date(ageDifMs); // miliseconds from epoch
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }
  currencyConcatinator = (symbol: string, amount: number, localFormat: string) => {
    return symbol + amount.toLocaleString(localFormat)
  }
  createFormData = (files: Array<any>): FormData => {
    let data = new FormData()
    for (const file of files) {
      data.append(file.name, file)
    }
    return data;
  }
  decodeJwt = (token: string) => {
    return jwtDecode(token)
  }
  statusClassDetector = (status: string) => {
    let tempStatus = status.toLowerCase()
    return tempStatus === "cancelled" ? "warning" : tempStatus === "confirmed" ? "info" : ""
  }
  datePatternChecker = (event: any) => {
    if (event.value.length === 3 || event.value.length === 6) {
      if (event.value[event.value.length - 1] !== "-") {
        let split: string;
        split = event.value.splice(event.value.length - 1, 1)
        event.control.setValue(split)
      }
    }
  }
  dateDisplayFormatConverter = (date: Date) => {
   return MONTHS[date.getMonth()] + " " + date.getDate() + ", " + date.getFullYear()
  }

  isFutureDate(dateString: string) {
    const inputDate = new Date(dateString);
    const currentDate = new Date();
    
    return inputDate > currentDate;
  }

  getFormatedDate(dateString: any) {
    const date = new Date(dateString);
    
    const options: any = { year: 'numeric', month: 'long', day: 'numeric' };
    const formattedDate = date.toLocaleDateString('en-US', options);
    
    return formattedDate;
  }

   getMemberServiceInfo(service: any) {

    return new Promise((resolve, reject)=> {

      let serviceName = encodeURIComponent(service);
      let endpoint = SERVER_URL + `/member/services?serviceName=${serviceName}`;  

      this.httpService.requestEntity('GET', endpoint)
      // .pipe(takeUntil(this.onDestroy$))
        .subscribe((res)=> {

          console.log(console.log(res));
          let response =  res.body;        
          resolve(response.data);

          // this.serviceAvailable = this.list && this.list.length != 0 && this.list.find((x: any) => x.serviceName == 'Surgical Centers') ? true: false;
          // console.log(this.serviceAvailable);
          
        });
      })
    }

  // showFutureServiceError(errMsg: any) {
  //   // this.showFutureServiceErr = true;
  //   this.sharedService.globalMembershipError.next({showErr: true, msg: errMsg});
  // }

  showFutureServiceError(serviceObj: any) {
    // this.showFutureServiceErr = true;
    let errMsg = `Please note that your membership will start on ${this.getFormatedDate(serviceObj.startDate)}. After that date, you will be able to fully access all of the membership services and benefits`;
    this.sharedService.globalMembershipError.next({showErr: true, msg: errMsg});
    window.scrollTo({top: 0,behavior: 'smooth'});
  }

  getRedirectUrl(alias: any, ) {
    let route = '';

    switch (alias) {
      case 'imaging':
        route = 'imaging';
        break;
    
      default:
        break;
    }

    return route;
  }

  getMemberTypeLabel(val: any) {
    let label = '';
    switch (val) {
      case 'INDIVIDUAL':
        label = 'Individual';
        break;

      case 'INDIVIDUAL_SPOUSE':
        label = 'Individual & Spouse';
        break;
          
      case 'FAMILY':
        label = 'Individual & Family';
        break;

      case 'INDIVIDUAL_CHILDREN':
        label = 'Individual & Children';
        break; 
    
      default:
        break;
    }

    return label;
  }

  login(payload: any, event?: any) {
    const body = { ...payload }
    delete body.rememberMe
    if (event) {
      this.sharedService.addLoader(event)
    }
    this.httpService.requestEntity('post', LOGIN, body).subscribe(
      (response: any) => {
        this.localStorageService.setDataInLocalStorage({ key: "token", value: response.body.data.jwt })
        localStorage.setItem("user_information", JSON.stringify(this.decodeJwt(this.localStorageService.getDataInLocalStorage("token"))))
        this.sharedService.removeLoader()
        // if (localStorage.getItem('addToCartData')) {
        //    this.sharedService.addToCart();
        // } else {
        if (localStorage.getItem('redirectionUrl')) {
          let route: any = this.localStorageService.getDataInLocalStorage('redirectionUrl')
          localStorage.removeItem('redirectionUrl');
          this.router.navigateByUrl(route);
        } else {
          this.router.navigateByUrl(ROUTES.profile)
        }
        // }


      }, (error: any) => {
        this.sharedService.removeLoader()
      }
    )
  }

  openTermUseModal() {
    this.termUseModalComp.open();
  }

  openPrivacyPoliciesModal() {
    this.privacyPolicyModalComp.open();
  }


}
